import React from 'react';
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { WithTranslation, withTranslation } from 'react-i18next';

import { connect } from 'react-redux';
import { Link as RouterLink, match } from 'react-router-dom';
// Ressources
import { Anchor } from '../../../components/anchor';
import { AnchorBlock } from '../../../components/anchorBlock';
import App from '../../../components/app';
import { Dialog } from '../../../components/dialog';
import ErrorMessage from '../../../components/error-message';
import EnhancedListItem from '../../../components/listItem';
import { TooltipButton } from '../../../components/tooltip-button';
import Listing from '../../../components/listing';
// Modules
import { BillPartner, RideDetails, TParams } from '../../../redux/reducers/api-types';
import { ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
import {
    actions as partnerBillActions,
    PartnerBillById,
    partnerBillStatus,
    selectors as partnerBillSelectors,
} from '../../../redux/reducers/partner-bills';
// Ressources
import DateUtils from '../../../utils/dateUtils';
import FormattingUtils from '../../../utils/formattingUtils';
import withGlobalStyles from '../../../utils/global-styles';
import UserUtils, { AccountRight, RightRank } from '../../../utils/userUtils';
import { paths } from '../../paths';
import { FormatType, ListCell } from '../../../local-types';

interface PartnerBillViewProps extends WithTranslation {
    globalClasses: any;
    match: match<TParams>;
    partnerBills: PartnerBillById;
    getPartnerBillsViewError: ErrorPayload;
    getPartnerBill: (id: string) => void;
    updatePartnerBillStatus: (id: string, status: any) => void;
}

class PartnerBillViewPage extends React.Component<PartnerBillViewProps> {
    isAdmin: boolean;

    constructor(props) {
        super(props);
        this.isAdmin = UserUtils.checkRightsOrAdmin({ name: AccountRight.PARTNER_BILLING_MGMT, mode: RightRank.W });
    }

    componentDidMount() {
        const { match, partnerBills, getPartnerBill } = this.props;
        const id = match.params.id;
        if (!partnerBills[id]) {
            getPartnerBill(id);
        }
    }

    handleStatus = (status: string) => () => {
        const { match, updatePartnerBillStatus } = this.props;
        const id = match.params.id;
        updatePartnerBillStatus(id, status);
    };

    //========================================//
    //=============== Builders ===============//
    //========================================//

    generateListItems = (partnerBillPayload: BillPartner): ListCell[] => {
        const { t } = this.props;

        if (!partnerBillPayload) {
            return [];
        }

        const listItems: ListCell[] = [
            {
                title: t('common:form.id'),
                data: partnerBillPayload.id,
            },
            {
                title: t('status'),
                data: t(`partner_bill_status.${partnerBillPayload.status}`),
            },
            {
                title: t('common:form.partner'),
                data: (
                    <Link
                        color="secondary"
                        component={RouterLink}
                        to={`${paths.partners}/${partnerBillPayload.partner?.id}`}
                    >
                        {partnerBillPayload.partner?.name}
                    </Link>
                ),
            },
            {
                title: t('period'),
                data: `${t('common:date_from')} ${DateUtils.tsToDate(
                    new Date(partnerBillPayload.start_date).getTime() / 1000,
                    true,
                )} ${t('common:date_to')} ${DateUtils.tsToDate(
                    new Date(partnerBillPayload.end_date).getTime() / 1000,
                    true,
                )}`,
            },
            {
                title: t('amount_ht'),
                data: partnerBillPayload.amount_pre_tax,
                type: FormatType.Price,
            },
            // {
            //     title: t('vat_rate'),
            //     data: partnerBillPayload.exempt_from_vat
            //         ? null
            //         : `${FormattingUtils.price(partnerBillPayload.vat_amount, true)} (${Math.round(
            //               partnerBillPayload.vat_rate * 100,
            //           )} %)`,
            // },
            {
                title: t('amount_ttc'),
                data: partnerBillPayload.amount_with_tax,
                type: FormatType.Price,
            },
            // {
            //     title: t('obp'),
            //     data: partnerBillPayload.obp,
            //     type: FormatType.Price,
            // },
            // {
            //     title: t('snappay'),
            //     data: partnerBillPayload.ep,
            //     type: FormatType.Price,
            // },
            // {
            //     title: t('tip'),
            //     data: partnerBillPayload.tip,
            //     type: FormatType.Price,
            // },
            {
                title: t('bill_pdf'),
                data: partnerBillPayload.document_path && (
                    <Link
                        color="secondary"
                        href={partnerBillPayload.document_path}
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        {t('common:download')}
                    </Link>
                ),
                optional: true,
            },
            {
                title: t('report_pdf'),
                data: partnerBillPayload.document_path && (
                    <Link
                        color="secondary"
                        href={`${partnerBillPayload.document_path}/report`}
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        {t('common:download')}
                    </Link>
                ),
                optional: true,
            },
            // {
            //     title: t('footer'),
            //     data: partnerBillPayload.additional_information,
            //     optional: true,
            // },
        ];
        return listItems;
    };

    renderListItems = (partnerBillPayload: BillPartner) => {
        const { t, globalClasses } = this.props;
        if (!partnerBillPayload) {
            return null;
        }

        return (
            <EnhancedListItem
                datas={this.generateListItems(partnerBillPayload)}
                title={t('common:form.general_infos')}
                lock
                cardActions={
                    this.isAdmin && (
                        <>
                            {/* {canEdit && (
                                <Button
                                    color="secondary"
                                    variant="contained"
                                    component={RouterLink}
                                    to={createPath(paths.billsPartnerEdit, { id })}
                                >
                                    {t('common:edit')}
                                </Button>
                            )} */}
                            {partnerBillPayload.status === partnerBillStatus.PENDING && (
                                <>
                                    {partnerBillPayload.amount_with_tax >= 0 && (
                                        <Dialog
                                            btnTitle={t('common:validate')}
                                            description={t('validate_bill_desc')}
                                            customButton={
                                                <TooltipButton title={t('common:validate')}>
                                                    <Button variant="contained" className={globalClasses.btnSuccess}>
                                                        {t('common:validate')}
                                                    </Button>
                                                </TooltipButton>
                                            }
                                            onOK={this.handleStatus(partnerBillStatus.VALIDATED)}
                                        />
                                    )}
                                    <Dialog
                                        btnTitle={t('blocked')}
                                        description={t('blocked_bill_desc')}
                                        customButton={
                                            <TooltipButton title={t('blocked')}>
                                                <Button variant="contained" className={globalClasses.btnDanger}>
                                                    {t('blocked')}
                                                </Button>
                                            </TooltipButton>
                                        }
                                        onOK={this.handleStatus(partnerBillStatus.BLOCKED)}
                                    />
                                </>
                            )}
                            {partnerBillPayload.status === partnerBillStatus.VALIDATED && (
                                <>
                                    {/* <Dialog
                                    btnTitle={t('mark_as_paid')}
                                    description={t('mark_as_paid_desc')}
                                    customButton={
                                        <TooltipButton title={t('mark_as_paid')}>
                                            <Button variant="contained" color="primary">
                                                {t('mark_as_paid')}
                                            </Button>
                                        </TooltipButton>
                                    }
                                    onOK={this.handleStatus(partnerBillStatus.SENT)}
                                /> */}
                                    <Dialog
                                        btnTitle={t('blocked')}
                                        description={t('blocked_bill_desc')}
                                        customButton={
                                            <TooltipButton title={t('blocked')}>
                                                <Button variant="contained" className={globalClasses.btnDanger}>
                                                    {t('blocked')}
                                                </Button>
                                            </TooltipButton>
                                        }
                                        onOK={this.handleStatus(partnerBillStatus.BLOCKED)}
                                    />
                                </>
                            )}
                            {partnerBillPayload.status === partnerBillStatus.BLOCKED &&
                                partnerBillPayload.amount_with_tax > 0 && (
                                    <Dialog
                                        btnTitle={t('common:validate')}
                                        description={t('validate_bill_desc')}
                                        customButton={
                                            <TooltipButton title={t('common:validate')}>
                                                <Button variant="contained" className={globalClasses.btnSuccess}>
                                                    {t('common:validate')}
                                                </Button>
                                            </TooltipButton>
                                        }
                                        onOK={this.handleStatus(partnerBillStatus.VALIDATED)}
                                    />
                                )}
                            {partnerBillPayload.status === partnerBillStatus.SENT &&
                                partnerBillPayload.amount_with_tax > 0 && (
                                    <Dialog
                                        btnTitle={t('cancel_sent')}
                                        description={t('cancel_sent_desc')}
                                        customButton={
                                            <TooltipButton title={t('cancel_sent')}>
                                                <Button variant="contained" className={globalClasses.btnDanger}>
                                                    {t('cancel_sent')}
                                                </Button>
                                            </TooltipButton>
                                        }
                                        onOK={this.handleStatus(partnerBillStatus.VALIDATED)}
                                    />
                                )}
                        </>
                    )
                }
            />
        );
    };

    renderCourseDetails = (course: RideDetails) => {
        const { t } = this.props;
        return (
            <div key={course.ride_number} style={{ display: 'flex' }}>
                <Link
                    color="secondary"
                    component={RouterLink}
                    to={`${paths.bookings}?search[number]=${course.ride_number}`}
                >
                    {t('booking:booking.number', { id: course.ride_number })} -{' '}
                    {DateUtils.tsToDate(new Date(course.date).getTime() / 1000)}
                </Link>
                <div style={{ flex: 1, textAlign: 'end' }}>{`${FormattingUtils.price(
                    course.pre_tax_partner,
                    true,
                )} HT`}</div>
            </div>
        );
    };

    renderDriverDetails = (partnerBillPayload: BillPartner) => {
        return partnerBillPayload.details?.rides?.totals_by_driver?.map((r) => (
            <Accordion key={r.driver.id}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`panel-${r.driver.id}`}
                    id={`panel-${r.driver.id}`}
                >
                    <Link color="secondary" component={RouterLink} to={`${paths.drivers}/${r.driver.id}`}>
                        {r.driver.name}
                    </Link>
                    <div style={{ flex: 1, textAlign: 'end' }}>{`${FormattingUtils.price(
                        r.pre_tax_partner,
                        true,
                    )} HT`}</div>
                </AccordionSummary>
                <AccordionDetails style={{ flexDirection: 'column' }}>
                    {partnerBillPayload.details?.rides?.details &&
                        partnerBillPayload.details.rides.details
                            .filter((d) => d.driver.id === r.driver.uuid)
                            .map((course) => this.renderCourseDetails(course))}
                </AccordionDetails>
            </Accordion>
        ));
    };

    renderRegulationDetails = (partnerBillPayload: BillPartner) => {
        const { t } = this.props;
        return (
            <Listing
                datas={partnerBillPayload.regulations_billing?.map((item, key) => (
                    <div key={key}>
                        <div>
                            {DateUtils.stringToDate(item.created_at)} - {item.author ? `${item.author.name} - ` : ''}
                            {item.label}
                        </div>
                        {item.with_tax_customer !== 0 && <div>Client : {item.with_tax_customer} € (TTC)</div>}
                        {item.with_tax_partner !== 0 && (
                            <div>
                                {item.partner?.name || 'Partenaire'} : {item.with_tax_partner} € (TTC)
                            </div>
                        )}
                        <div>{t(`booking:regulation_type.${item.product_type}`)}</div>
                        {item.ride_id ? (
                            <Link color="secondary" component={RouterLink} to={`${paths.bookings}/${item.ride_id}`}>
                                {t('booking:booking.number', { id: item.ride_id })}
                            </Link>
                        ) : (
                            <div>{t('booking:booking.none')}</div>
                        )}
                    </div>
                ))}
                customEmpty={t('regulation.none')}
            />
        );
    };

    render() {
        const { t, match, partnerBills, getPartnerBillsViewError } = this.props;
        const id = match.params.id;
        const partnerBillPayload: BillPartner = partnerBills[id];

        return (
            <App
                needRights={AccountRight.PARTNER_BILLING_MGMT}
                header={{
                    icon: 'default',
                    title: partnerBillPayload && t('view.title') + ' ' + partnerBillPayload?.bill_number,
                    anchors: [
                        { href: '#general_infos', label: t('common:form.general_infos') },
                        { href: '#bill_details', label: t('bill_details') },
                    ],
                }}
            >
                <ErrorMessage payload={getPartnerBillsViewError} />
                {partnerBillPayload && (
                    <>
                        <br />
                        <Anchor id="general_infos" />
                        {this.renderListItems(partnerBillPayload)}
                        <br />
                        <AnchorBlock
                            id="bill_details"
                            title={t('regulation.title')}
                            items={this.renderRegulationDetails(partnerBillPayload)}
                        />
                        <AnchorBlock
                            id="regulations"
                            title={t('bill_details')}
                            items={this.renderDriverDetails(partnerBillPayload)}
                        />
                    </>
                )}
            </App>
        );
    }
}

const PartnerBillViewPageWithTranslation = withGlobalStyles(
    withTranslation(['partner_bill', 'booking', 'common'])(PartnerBillViewPage),
);

const mapStateToProps = (state) => ({
    partnerBills: partnerBillSelectors.partnerBills(state),
    getPartnerBillsViewError: errorSelectors.getPartnerBillsViewError(state),
});

const mapDispatchToProps = {
    getPartnerBill: partnerBillActions.getPartnerBill,
    updatePartnerBillStatus: partnerBillActions.updatePartnerBillStatus,
};

export default connect(mapStateToProps, mapDispatchToProps)(PartnerBillViewPageWithTranslation);
